import React, { useRef, useEffect, useState, useMemo } from "react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import PubSub from "pubsub-js"
import DropDown from "aldoo-ra/Dropdown"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Spinner from "aldoo-ra/Spinner"
import Checkbox from "aldoo-ra/Checkbox"
import Tooltip from "aldoo-ra/Tooltip"
import { getFieldByLocalizationKey } from "../get-cms-info-by-key"
import { getFieldEditor } from "aldoo-ra/CMS/field-editor-resolver"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import { FreezeTranslation, AutoTranslateKey } from "../api"
import SnowflakeIcon, { getFreezeState, getFreezeTitle } from "../snowflake-icon"
import { showNotification } from "aldoo-ra/Notification"
import LocalizationAIRewriteDialog from "../localization-ai-rewrite-dialog"

// Custom ObjectEditor component with freeze controls for localization
const LocalizationObjectEditor = React.forwardRef(({
  onChange,
  value,
  field,
  fieldFreezeStates = {},
  onFieldFreezeToggle,
  readOnly = false,
  showInfo = true,
  parentObject,
  showClearBtn = true,
  showAddBtn = true,
  showItemDelete = true,
  onClearRef,
  primaryValue = [],
  primaryLanguage = "",
  controller,
  selectedLang = "",
  localizationKey = ""
}, ref) => {
  const getInitialValue = () => {
    if (Array.isArray(value) && value.length > 0) return value
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value).map(([key, val]) => ({ key, value: val }))
    }
    return [{ key: "", value: "" }]
  }

  const [localValue, setLocalValue] = useState(getInitialValue)
  const inputRefs = useRef([])
  const isInternalChange = useRef(false)
  const [fieldReferenceStates, setFieldReferenceStates] = useState({}) // Track which fields show reference
  const [translatingFields, setTranslatingFields] = useState({}) // Track per-field auto-translate loading
  const [translatingFieldsAll, setTranslatingFieldsAll] = useState({}) // Track per-field all-languages loading
  const [aiRewriteField, setAiRewriteField] = useState(null) // fieldKey currently open in AI rewrite dialog

  // Detect duplicate keys live. The bundle saves as an object (last-key-wins),
  // so duplicates silently overwrite — surface them to the editor.
  const dupKeySet = useMemo(() => {
    const counts = new Map()
    for (const item of localValue || []) {
      const k = item?.key
      if (typeof k !== "string" || !k.length) continue
      counts.set(k, (counts.get(k) || 0) + 1)
    }
    const dups = new Set()
    for (const [k, n] of counts) if (n > 1) dups.add(k)
    return dups
  }, [localValue])

  const isRequired = typeof field.required === "function" ? field.required(parentObject) : field.required

  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  useEffect(() => {
    if (!isInternalChange.current) {
      const newValue = getInitialValue()
      setLocalValue(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(newValue)) return newValue
        return prev
      })
    }
    isInternalChange.current = false
  }, [value, selectedLang])

  const handleValueChange = (key, updatedValue, index) => {
    try {
      const updatedValues = [...localValue]
      if (!updatedValues[index]) {
        updatedValues[index] = {}
      }
      updatedValues[index] = {
        ...updatedValues[index],
        [key]: updatedValue,
      }
      updateValue(updatedValues)
    } catch (error) {
    }
  }

  const addNewInput = () => {
    if (readOnly) return
    const newValues = [...localValue, { key: "", value: "" }]
    updateValue(newValues)
    
    setTimeout(() => {
      const lastInputIndex = localValue.length
      inputRefs.current[lastInputIndex]?.key?.focus()
    }, 0)
  }

  const removeInput = (index) => {
    if (readOnly) return
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    if (readOnly) return
    updateValue([])
  }

  React.useImperativeHandle(ref, () => ({
    addItem: addNewInput,
    clearAll: clearAll
  }), [])

  const handleKeyDown = (e, index) => {
    if (readOnly) return
    if (e.key === "Enter") {
      e.preventDefault()
      const trimmed = e.target.value.trim()
      if (trimmed !== e.target.value) {
        handleValueChange("key", trimmed, index)
      }
      addNewInput()
    }
  }

  const handleKeyInputBlur = (e, index) => {
    const trimmed = e.target.value.trim()
    if (trimmed !== e.target.value) {
      handleValueChange("key", trimmed, index)
    }
  }

  // Helper function to find primary language value for a key
  const getPrimaryValueForKey = (key) => {
    if (!Array.isArray(primaryValue) || !key) return null
    const primaryItem = primaryValue.find(item => item.key === key)
    return primaryItem?.value || null
  }

  // Toggle reference display for a specific field
  const toggleFieldReference = (fieldKey) => {
    setFieldReferenceStates(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }))
  }

  // Auto-translate a single field value to the selected language
  const autoTranslateField = async (fieldKey) => {
    if (!fieldKey || !controller?.primaryLanguage || !selectedLang) return
    setTranslatingFields(prev => ({ ...prev, [fieldKey]: true }))
    try {
      const result = await AutoTranslateKey({
        key: localizationKey,
        languageIn: controller.primaryLanguage,
        languageOut: selectedLang,
        option: 'key',
        fieldKey,
      })
      if (result?.translatedValue !== undefined) {
        const updated = localValue.map(item =>
          item.key === fieldKey ? { ...item, value: result.translatedValue } : item
        )
        updateValue(updated)
      } else if (result?.error === 'no_license_key') {
        showNotification({ message: 'Please install a DeepL license key first', className: 'bg-admin_error text-white' })
      } else if (result?.error) {
        showNotification({ message: result.error, className: 'bg-admin_error text-white' })
      }
    } catch {
      showNotification({ message: 'Auto-translate failed', className: 'bg-admin_error text-white' })
    } finally {
      setTranslatingFields(prev => ({ ...prev, [fieldKey]: false }))
    }
  }

  // Auto-translate a single field to all languages from primary
  const autoTranslateFieldAllLanguages = async (fieldKey) => {
    if (!fieldKey || !controller?.primaryLanguage) return
    setTranslatingFieldsAll(prev => ({ ...prev, [fieldKey]: true }))
    try {
      const result = await AutoTranslateKey({
        key: localizationKey,
        languageIn: controller.primaryLanguage,
        languageOut: '',
        option: 'all',
        fieldKey,
      })
      if (result?.error === 'no_license_key') {
        showNotification({ message: 'Please install a DeepL license key first', className: 'bg-admin_error text-white' })
        return
      }
      if (result?.error) {
        showNotification({ message: result.error, className: 'bg-admin_error text-white' })
        return
      }
      // Fetch fresh pre-formatted translations and broadcast to parent so all language views update
      const fresh = await controller.getLocalizationKeyPreFormat(localizationKey)
      const freshTranslations = fresh?.translations
      if (freshTranslations) {
        PubSub.publish('localization.auto-translate-completed', {
          localizationKey,
          translations: freshTranslations,
        })
      }
      showNotification({ message: `"${fieldKey}" translated in all languages`, className: 'bg-admin_primary text-admin_text_inv' })
    } catch {
      showNotification({ message: 'Auto-translate failed', className: 'bg-admin_error text-white' })
    } finally {
      setTranslatingFieldsAll(prev => ({ ...prev, [fieldKey]: false }))
    }
  }

  const renderItem = (item, index) => {
    if (!item) return null

    const fieldKey = item.key || ""
    const isDup = !!fieldKey && dupKeySet.has(fieldKey)
    // Field is frozen if language is frozen with "*" OR if field is specifically frozen
    const isFieldFrozen = fieldFreezeStates["*"] === true || fieldFreezeStates[fieldKey] === true
    
    // Get primary language value for reference
    const primaryRef = getPrimaryValueForKey(fieldKey)
    const showFieldReference = fieldReferenceStates[fieldKey]
    const isNonPrimaryLanguage = selectedLang && selectedLang !== primaryLanguage

    return (
      <div key={index} className={`w-full flex flex-row items-center gap-2 mt-2 ${isDup ? "rounded-md ring-1 ring-admin_error/60 px-1 py-1 -mx-1" : ""}`}>
        <div className="w-1/3">
          <Input
            variant="admin"
            readOnly={readOnly}
            ref={(el) => (inputRefs.current[index] = { ...inputRefs.current[index], key: el })}
            className={`w-full text-admin_text bg-admin_paper ${isDup ? "!border-admin_error" : ""}`}
            value={item.key || ""}
            placeholder="Key"
            onChange={(e) => handleValueChange("key", e.target.value, index)}
            onBlur={(e) => handleKeyInputBlur(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          />
        </div>

        {isDup && (
          <Tooltip content="Only the LAST entry's value will be saved" variant="admin">
            <span className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-admin_error/10 text-admin_error text-xs font-medium whitespace-nowrap flex-shrink-0">
              <ExclamationTriangleIcon className="w-4 h-4" />
              Duplicate key
            </span>
          </Tooltip>
        )}
        
        <div className="flex-grow relative">
          <Input
            variant={showFieldReference ? "admin_preview" : "admin"}
            readOnly={readOnly || showFieldReference}
            ref={(el) => (inputRefs.current[index] = { ...inputRefs.current[index], value: el })}
            className={`w-full text-admin_text ${primaryRef && showFieldReference ? 'pr-12' : ''}`}
            value={showFieldReference ? primaryRef : (item.value || "")}
            placeholder={showFieldReference ? `Preview: ${controller?.getLanguageName(primaryLanguage) || 'English'}` : "Value"}
            onChange={showFieldReference ? undefined : (e) => handleValueChange("value", e.target.value, index)}
            onKeyDown={showFieldReference ? undefined : (e) => handleKeyDown(e, index)}
          />
          {/* English Reference Overlay */}
          {primaryRef && showFieldReference && (
            <div className="absolute top-1/2 right-2 transform -translate-y-1/2 z-10">
              <span 
                className="inline-block px-2 py-1 text-xs bg-admin_primary text-admin_text_inv rounded-full cursor-pointer select-all hover:bg-admin_primary/80 transition-colors"
                title={`${controller?.getLanguageName(primaryLanguage) || 'English'}: ${primaryRef}`}
                onClick={() => {
                  // Copy to clipboard when clicked
                  navigator.clipboard.writeText(primaryRef)
                }}
              >
                {controller?.getLanguageName(primaryLanguage) || 'EN'}
              </span>
            </div>
          )}
        </div>

        {/* Per-field Reference Checkbox - only for non-primary languages and when primary value exists */}
        {isNonPrimaryLanguage && primaryRef && fieldKey && !readOnly && (
          <div className="flex-shrink-0">
            <Tooltip
              content={showFieldReference 
                ? `View in ${controller?.getLanguageName(selectedLang) || 'current language'}`
                : `Preview in ${controller?.getLanguageName(primaryLanguage) || 'English'}`}
              variant="admin"
            >
              <div className="relative">
                {/* Custom checkbox with book icon */}
                <button
                  onClick={() => toggleFieldReference(fieldKey)}
                  className={`w-6 h-6 border border-admin_bordered rounded-full flex items-center justify-center cursor-pointer hover:bg-admin_neutral_1 transition-colors ${
                    showFieldReference 
                      ? 'bg-admin_primary border-admin_primary' 
                      : 'bg-transparent'
                  }`}
                >
                  {showFieldReference ? (
                    <span className="text-xs text-admin_text_inv">✓</span>
                  ) : (
                    <span className="text-xs text-admin_text">📖</span>
                  )}
                </button>
              </div>
            </Tooltip>
          </div>
        )}

        {/* Auto-translate button for this specific field — only for non-primary languages with a primary value */}
        {isNonPrimaryLanguage && primaryRef && fieldKey && !readOnly && (
          <div className="flex-shrink-0">
            <Tooltip
              content="Auto translate this key/pair to primary language"
              variant="admin"
            >
              <button
                onClick={() => autoTranslateField(fieldKey)}
                disabled={translatingFields[fieldKey]}
                className={`w-6 h-6 border border-admin_bordered rounded-full flex items-center justify-center cursor-pointer hover:bg-admin_neutral_1 transition-colors text-xs font-bold ${
                  translatingFields[fieldKey]
                    ? 'bg-admin_primary border-admin_primary text-admin_text_inv opacity-60 cursor-wait'
                    : 'bg-transparent text-admin_text'
                }`}
              >
                {translatingFields[fieldKey] ? '…' : 'A'}
              </button>
            </Tooltip>
          </div>
        )}

        {/* Translate this field to ALL languages from primary */}
        {isNonPrimaryLanguage && primaryRef && fieldKey && !readOnly && (
          <div className="flex-shrink-0">
            <Tooltip
              content="Translate again this key/value pair in all languages from primary"
              variant="admin"
            >
              <button
                onClick={() => autoTranslateFieldAllLanguages(fieldKey)}
                disabled={translatingFieldsAll[fieldKey]}
                className={`w-6 h-6 border border-admin_bordered rounded-full flex items-center justify-center cursor-pointer hover:bg-admin_neutral_1 transition-colors text-xs font-bold ${
                  translatingFieldsAll[fieldKey]
                    ? 'bg-admin_primary border-admin_primary text-admin_text_inv opacity-60 cursor-wait'
                    : 'bg-transparent text-admin_text'
                }`}
              >
                {translatingFieldsAll[fieldKey] ? (
                  <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 20v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                  </svg>
                ) : 'A+'}
              </button>
            </Tooltip>
          </div>
        )}

        {/* AI Rewrite button for this field */}
        {fieldKey && !readOnly && (
          <div className="flex-shrink-0">
            <Tooltip content="AI rewrite this value" variant="admin">
              <button
                onClick={() => setAiRewriteField(fieldKey)}
                className="w-7 h-7 border border-admin_bordered rounded-full flex items-center justify-center cursor-pointer hover:bg-admin_neutral_1 transition-colors bg-transparent text-admin_primary"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a3 3 0 0 1 3 3v1h.5a1.5 1.5 0 0 1 0 3H19v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1h-.5a1.5 1.5 0 0 1 0-3H5v-1a3 3 0 0 1 3-3h3V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zm-2 9a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm4 0a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm-5 5v1h6v-1H9z"/>
                </svg>
              </button>
            </Tooltip>
          </div>
        )}

        {/* Field-level freeze control - only show if field has a key */}
        {fieldKey && fieldKey.trim() !== "" && onFieldFreezeToggle && (() => {
          const freezeState = isFieldFrozen ? "frozen" : "unfrozen"
          const freezeTitle = isFieldFrozen 
            ? "Allow auto-translation to modify this field"
            : "Prevent auto-translation from modifying this field"
          
          return (
            <Tooltip
              content={freezeTitle}
              variant="admin"
            >
              <SnowflakeIcon
                state={freezeState}
                className="flex-shrink-0"
                onClick={() => {
                  onFieldFreezeToggle(fieldKey)
                }}
              />
            </Tooltip>
          )
        })()}

        {!readOnly && showItemDelete && (
          <Button
            variant="text"
            className="text-admin_text_secondary hover:text-admin_text rounded-full flex-shrink-0"
            onClick={() => removeInput(index)}
          >
            <span className="text-lg">×</span>
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="object-editor w-full flex-1">
      {/* AI Rewrite dialog — mounted at editor level so it has access to localValue/updateValue */}
      {aiRewriteField && (
        <LocalizationAIRewriteDialog
          originalText={localValue.find(item => item.key === aiRewriteField)?.value || ""}
          onAccept={(rewritten) => {
            updateValue(localValue.map(item =>
              item.key === aiRewriteField ? { ...item, value: rewritten } : item
            ))
          }}
          onClose={() => setAiRewriteField(null)}
        />
      )}

      <div className="ml-3 flex items-center gap-3">
        <span className="text-sm text-admin_text_secondary">
          {localValue.length} {localValue.length === 1 ? "item:" : "items:"}
        </span>
        {dupKeySet.size > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-admin_error">
            <ExclamationTriangleIcon className="w-4 h-4" />
            {dupKeySet.size} duplicate {dupKeySet.size === 1 ? "key" : "keys"}
          </span>
        )}
      </div>

      {Array.isArray(localValue) && localValue.map((item, index) => renderItem(item, index))}

      <div className="flex flex-row justify-between items-center mt-4">
        {showInfo && (
          <div className="flex flex-col">
            <label className="block font-semibold text-admin_text">
              {field.label}
              {readOnly && <span className="ml-2 text-admin_warning">(read-only)</span>}
              {isRequired && <span className="ml-2 text-error">(required)</span>}
            </label>
            {field.description && (
              <label className="block text-sm text-admin_text">{field.description}</label>
            )}
          </div>
        )}

        <div className="flex items-center space-x-2">
          {!readOnly && showAddBtn && (
            <Button variant="admin_secondary" onClick={addNewInput}>
              Add
            </Button>
          )}
          {!readOnly && showClearBtn && localValue.length > 0 && (
            <Button variant="admin_secondary" onClick={clearAll}>
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  )
})


/**
 * ObjectItemEditor Component
 *
 * @param {Object} props
 * @param {Object} translations - The translation data for the languages
 * @param {Function} handleContentChange - Function to handle content change for the selected language
 * @param {String} selectedLang - The currently selected language code
 * @param {String} primaryLanguage - The primary language code
 * @param {Object} controller - The controller object containing necessary methods and data for translations
 */
export default function ObjectItemEditor({
  localizationKey,
  translations,
  handleContentChange,
  selectedLang,
  primaryLanguage,
  controller,
  onFreezeToggle,
  pendingFreezeChanges = {},
  item,
  freezeStates = {},
}) {
  const fieldEditorRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [fieldFreezeStates, setFieldFreezeStates] = useState({}) // Track field-level freeze states
  const [freshTranslations, setFreshTranslations] = useState(null) // Store fresh translations from PubSub
  const [importOpen, setImportOpen] = useState(false)
  const [importMode, setImportMode] = useState("kv") // 'kv' | 'keys' | 'values'
  const [importText, setImportText] = useState("")

  // Subscribe to auto-translate completion events
  useEffect(() => {
    const token = PubSub.subscribe('localization.auto-translate-completed', (msg, data) => {
      if (data.localizationKey === localizationKey) {
        setFreshTranslations(data.translations)
      }
    })

    return () => PubSub.unsubscribe(token)
  }, [localizationKey, selectedLang])
  
  //get the field by name from the fieldInfo
  //contains the field information such as id, name, type, etc.
  let field = getFieldByLocalizationKey(localizationKey)

  //set a default field if not found
  if (!field)
    field = { type: "Object", multiple: false, default: "", multiline: true }

  //not yet loaded
  if (!field) return <Spinner />

  const FieldEditor = getFieldEditor(field)

  const defaultValue = field.multiple ? [] : field.default || ""
  //get the primary language value and fieldInfo - use fresh translations if available
  const activeTranslations = freshTranslations || translations
  const primaryValue = activeTranslations[primaryLanguage]?.content || defaultValue

  // Key synchronization function - ensures all languages have the same keys as primary
  const keyAutoSync = React.useCallback((targetTranslations, primaryLangCode) => {
    if (!targetTranslations || !primaryLangCode || !targetTranslations[primaryLangCode]) {
      return targetTranslations
    }

    const primaryContent = targetTranslations[primaryLangCode].content || defaultValue
    if (!Array.isArray(primaryContent)) {
      return targetTranslations
    }

    // Extract keys from primary language
    const primaryKeys = primaryContent.map(item => item.key).filter(key => key && key.trim() !== "")

    // Create a synchronized copy of translations
    const syncedTranslations = { ...targetTranslations }

    // Sync all other languages
    Object.keys(syncedTranslations).forEach(langCode => {
      if (langCode === primaryLangCode) return // Skip primary language

      const langContent = syncedTranslations[langCode]?.content || defaultValue
      if (!Array.isArray(langContent)) return

      // Create a map of existing values for this language
      const existingValues = {}
      langContent.forEach(item => {
        if (item.key && item.key.trim() !== "") {
          existingValues[item.key] = item.value || ""
        }
      })

      // Create new content with keys matching primary language
      const syncedContent = primaryKeys.map(key => ({
        key,
        value: existingValues[key] || "" // Keep existing value or empty string for new keys
      }))

      // Update the language content
      syncedTranslations[langCode] = {
        ...syncedTranslations[langCode],
        content: syncedContent
      }
    })

    return syncedTranslations
  }, [defaultValue])

  // Track when auto-translate just completed to prevent immediate sync interference
  const [autoTranslateJustCompleted, setAutoTranslateJustCompleted] = React.useState(false)

  // Apply key synchronization whenever translations change
  const syncedTranslations = React.useMemo(() => {

    // If we have fresh translations from PubSub, use them directly without sync
    if (freshTranslations) {
      return activeTranslations
    }

    // During auto-translate OR just after completion, use translations directly to avoid interference
    if (controller?.isAutoTranslating || autoTranslateJustCompleted) {
      return activeTranslations
    }

    return keyAutoSync(activeTranslations, primaryLanguage)
  }, [activeTranslations, primaryLanguage, keyAutoSync, controller?.isAutoTranslating, autoTranslateJustCompleted, freshTranslations])

  React.useEffect(() => {
    if (!controller?.isAutoTranslating && autoTranslateJustCompleted) {
      // Auto-translate just finished, wait a bit before allowing sync
      const timer = setTimeout(() => {
        setAutoTranslateJustCompleted(false)
      }, 500) // 500ms delay
      return () => clearTimeout(timer)
    } else if (controller?.isAutoTranslating) {
      setAutoTranslateJustCompleted(true)
    }
  }, [controller?.isAutoTranslating, autoTranslateJustCompleted])

  // Immediate key synchronization when language is selected
  React.useEffect(() => {
    if (!selectedLang || !primaryLanguage || !translations[primaryLanguage]?.content) {
      return
    }

    // Skip if we're on the primary language
    if (selectedLang === primaryLanguage) {
      return
    }

    // Don't sync if controller is auto-translating - let auto-translate complete first
    if (controller?.isAutoTranslating) {
      return
    }

    // Don't sync immediately after auto-translate completes
    if (autoTranslateJustCompleted) {
      return
    }

    const primaryContent = activeTranslations[primaryLanguage].content || defaultValue
    const currentContent = activeTranslations[selectedLang]?.content || defaultValue

    if (Array.isArray(primaryContent)) {
      const primaryKeys = primaryContent.map(item => item?.key).filter(key => key && key.trim() !== "")

      // If primary has keys but current language doesn't, or keys don't match
      if (primaryKeys.length > 0) {
        const currentKeys = Array.isArray(currentContent) ?
          currentContent.map(item => item?.key).filter(key => key && key.trim() !== "") : []

        // Check if keys are out of sync
        const keysMatch = primaryKeys.length === currentKeys.length &&
          primaryKeys.every(key => currentKeys.includes(key)) &&
          currentKeys.every(key => primaryKeys.includes(key))

        if (!keysMatch) {

          // Create synchronized content immediately
          const existingValues = {}
          if (Array.isArray(currentContent)) {
            currentContent.forEach(item => {
              if (item?.key && item.key.trim() !== "") {
                existingValues[item.key] = item.value || ""
              }
            })
          }

          // Create new content with keys matching primary language
          const syncedContent = primaryKeys.map(key => ({
            key,
            value: existingValues[key] || ""
          }))

          // Apply synchronization immediately
          handleContentChange(syncedContent, selectedLang)
        }
      } else if (Array.isArray(currentContent) && currentContent.length > 0) {
        // If primary is empty but current language has content, clear it
        handleContentChange([], selectedLang)
      }
    }
  }, [selectedLang, primaryLanguage, activeTranslations, handleContentChange, defaultValue, controller?.isAutoTranslating, autoTranslateJustCompleted])

  // Secondary effect to handle translations updates when not switching languages
  React.useEffect(() => {
    if (!selectedLang || !primaryLanguage || selectedLang === primaryLanguage) {
      return
    }

    // Don't interfere during auto-translate
    if (controller?.isAutoTranslating) {
      return
    }

    // Don't sync immediately after auto-translate completes
    if (autoTranslateJustCompleted) {
      return
    }

    const primaryContent = activeTranslations[primaryLanguage]?.content || defaultValue
    const currentContent = activeTranslations[selectedLang]?.content || defaultValue

    if (Array.isArray(primaryContent) && Array.isArray(currentContent)) {
      const primaryKeys = primaryContent.map(item => item?.key).filter(key => key && key.trim() !== "")
      const currentKeys = currentContent.map(item => item?.key).filter(key => key && key.trim() !== "")

      // Only sync if this is a content update (not a language switch)
      const keysMatch = primaryKeys.length === currentKeys.length &&
        primaryKeys.every(key => currentKeys.includes(key)) &&
        currentKeys.every(key => primaryKeys.includes(key))

      if (!keysMatch) {

        const existingValues = {}
        currentContent.forEach(item => {
          if (item?.key && item.key.trim() !== "") {
            existingValues[item.key] = item.value || ""
          }
        })

        const syncedContent = primaryKeys.map(key => ({
          key,
          value: existingValues[key] || ""
        }))

        if (JSON.stringify(currentContent) !== JSON.stringify(syncedContent)) {
          handleContentChange(syncedContent, selectedLang)
        }
      }
    }
  }, [activeTranslations, selectedLang, primaryLanguage, handleContentChange, defaultValue, controller?.isAutoTranslating, autoTranslateJustCompleted])

  // Force re-render state for auto-translate updates
  const [forceUpdateKey, setForceUpdateKey] = React.useState(0)

  // Effect to handle post-auto-translate refresh
  React.useEffect(() => {
    // When auto-translate finishes, ensure we have the latest translations
    if (!controller?.isAutoTranslating && selectedLang && activeTranslations[selectedLang]) {
      // Force a re-render to ensure the new values are displayed
      setForceUpdateKey(prev => prev + 1)
    }
  }, [controller?.isAutoTranslating, selectedLang, activeTranslations])

  const value = syncedTranslations[selectedLang]?.content || defaultValue


  // Initialize field freeze states from parent freezeStates and pending changes
  useEffect(() => {
    if (!selectedLang) {
      setFieldFreezeStates({})
      return
    }
    
    // Start with the parent's freeze states for this language
    const parentFreezeState = freezeStates[selectedLang] || []
    const initialFreezeStates = {}
    
    if (Array.isArray(parentFreezeState)) {
      // If frozen array contains "*", all fields are frozen at language level
      if (parentFreezeState.includes("*")) {
        initialFreezeStates["*"] = true
      } else {
        // Convert array format to object format for easy lookup
        parentFreezeState.forEach(fieldKey => {
          if (fieldKey && fieldKey !== "*") {
            initialFreezeStates[fieldKey] = true
          }
        })
      }
    }
    
    // Apply pending freeze changes for this language - these override parent state
    Object.entries(pendingFreezeChanges).forEach(([changeKey, change]) => {
      if (change.languageCode === selectedLang && change.fieldKey) {
        if (change.frozen) {
          initialFreezeStates[change.fieldKey] = true
        } else {
          delete initialFreezeStates[change.fieldKey]
          // If we're unfreezing and everything was frozen ("*"), handle that
          if (initialFreezeStates["*"] === true) {
            delete initialFreezeStates["*"]
            // We would need all field keys to freeze the rest, for now just remove the wildcard
          }
        }
      }
    })
    
    setFieldFreezeStates(initialFreezeStates)
  }, [freezeStates, selectedLang, pendingFreezeChanges])

  // Handle freeze toggle for individual fields - now with pending state
  const handleFieldFreezeToggle = (fieldKey) => {
    if (!fieldKey || !onFreezeToggle) {
      return
    }
    
    // Use proper baseline from fieldFreezeStates (already initialized with server values)
    const currentState = fieldFreezeStates["*"] === true || fieldFreezeStates[fieldKey] === true
    const newFrozenState = !currentState
    
    
    // Update local field freeze states immediately for visual feedback
    setFieldFreezeStates(prev => {
      const newStates = { ...prev }
      
      if (newFrozenState) {
        // Add field to frozen list
        newStates[fieldKey] = true
      } else {
        // Remove field from frozen list and handle "*" case
        if (prev["*"] === true) {
          // If everything was frozen ("*"), we need to freeze all other fields except this one
          delete newStates["*"]
          // We would need the full list of fields here - for now just remove the field
        }
        delete newStates[fieldKey]
      }
      
      return newStates
    })
    
    // Call the parent component's freeze toggle handler with proper context
    onFreezeToggle(selectedLang, fieldKey)
  }

  // Enhanced handleContentChange that synchronizes keys across languages
  const handleContentChangeWithSync = React.useCallback((newContent, isPartialUpdate = false, originalFullContent = null) => {
    // If this is a partial update (filtered view), merge it with the original full content
    let finalContent = newContent
    if (isPartialUpdate && originalFullContent && Array.isArray(originalFullContent) && Array.isArray(newContent)) {
      // Create a map of updated values by key
      const updatedMap = new Map(newContent.map(item => [item.key, item]))

      // Merge: keep all original items, but update those that exist in the filtered set
      finalContent = originalFullContent.map(item =>
        updatedMap.has(item.key) ? updatedMap.get(item.key) : item
      )
    }

    // Always update the current language first
    handleContentChange(finalContent)

    // If this is the primary language, we need to sync keys to all other languages
    if (selectedLang === primaryLanguage && Array.isArray(finalContent)) {
      // Create updated translations with the new primary language content
      const updatedTranslations = {
        ...translations,
        [primaryLanguage]: {
          ...translations[primaryLanguage],
          content: finalContent
        }
      }

      // Apply key synchronization to ensure all languages have matching keys
      const resynced = keyAutoSync(updatedTranslations, primaryLanguage)

      // Batch update all other languages that were synced
      setTimeout(() => {
        Object.keys(resynced).forEach(langCode => {
          if (langCode !== primaryLanguage) {
            const originalContent = translations[langCode]?.content || defaultValue
            const syncedContent = resynced[langCode]?.content || defaultValue

            // Only update if the synced content is actually different
            if (JSON.stringify(originalContent) !== JSON.stringify(syncedContent)) {
              handleContentChange(syncedContent, langCode)
            }
          }
        })
      }, 50) // Small delay to ensure primary language update is processed first
    }
  }, [handleContentChange, selectedLang, primaryLanguage, translations, keyAutoSync, defaultValue])

  // Bulk import (primary language). Paste one entry per line: Key/Value splits
  // on the first comma; Keys-only / Values-only fill that column of existing
  // rows in order and append extras. Routes through the key-sync handler so the
  // imported keys propagate to every language.
  const importToPairs = (v) => {
    if (Array.isArray(v)) return v.map((p) => ({ key: p?.key ?? "", value: p?.value ?? "" }))
    if (v && typeof v === "object") return Object.entries(v).map(([key, val]) => ({ key, value: val }))
    return []
  }
  const handleImport = () => {
    const lines = importText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    if (!lines.length) { setImportOpen(false); return }
    const current = importToPairs(value)
    const stripEmpty = (rows) => {
      const out = [...rows]
      while (out.length && !out[out.length - 1]?.key && !out[out.length - 1]?.value) out.pop()
      return out
    }
    let rows
    if (importMode === "kv") {
      const pairs = lines.map((line) => {
        const i = line.indexOf(",")
        return i === -1
          ? { key: line, value: "" }
          : { key: line.slice(0, i).trim(), value: line.slice(i + 1).trim() }
      })
      rows = [...stripEmpty(current), ...pairs]
    } else {
      const col = importMode === "keys" ? "key" : "value"
      rows = current.map((r) => ({ ...r }))
      lines.forEach((val, i) => {
        if (i < rows.length) rows[i] = { ...rows[i], [col]: val }
        else rows.push(col === "key" ? { key: val, value: "" } : { key: "", value: val })
      })
      rows = stripEmpty(rows)
    }
    setSearchTerm("")
    handleContentChangeWithSync(rows, false)
    setImportText("")
    setImportOpen(false)
  }

  // Filter the value based on search term
  const filteredValue = React.useMemo(() => {
    const result = (!searchTerm || !Array.isArray(value)) ? value : value.filter(item => {
      if (!item || typeof item !== 'object') return false
      const keyMatch = item.key && item.key.toLowerCase().includes(searchTerm.toLowerCase())
      const valueMatch = item.value && item.value.toLowerCase().includes(searchTerm.toLowerCase())
      return keyMatch || valueMatch
    })


    return result
  }, [value, searchTerm, selectedLang])

  // Track if we're currently showing a filtered view
  const isFiltered = searchTerm && Array.isArray(value) && filteredValue.length < value.length

  return (
    <div className="min-h-[500px]">
      <div className="flex justify-between flex-col gap-2">
        <div className="flex flex-row justify-between items-center">
          {/* Display the selected language name */}
          <span className="font-semibold text-admin_text text-lg">
            {controller.getLanguageName(selectedLang)}
          </span>
          {/* Search, Auto-translate dropdown and action buttons */}
          <div className="flex flex-row items-center gap-3">
            <Input
              placeholder="Search variables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              showClearButton={true}
              className="w-[175px] xl:w-[190px]"
            />
            <LoadingOverlay
              title="Translating"
              message="Please wait while we translate the content"
              loading={controller.isAutoTranslating}
            />
            <DropDown
              className="w-[160px] xl:w-[190px]"
              isMenu={true}
              placeholder="Auto Translate"
              options={
                //if the selected language is the primary language, do not show the primary language in the auto-translate options
                selectedLang === primaryLanguage
                  ? controller.autoTranslateOptions.filter(
                      (option) => option.value !== "key"
                    )
                  : controller.autoTranslateOptions
              }
              onChange={(item) => {
                controller.setSelectedAutoTranslateOption(item.value)
              }}
              sortBy="index"
              sortDirection="desc"
              menuPosition="bottom"
              onItemRender={(item) => item.name}
            />
            {selectedLang === primaryLanguage && (
              <div className="relative">
                <Button
                  variant="admin_secondary"
                  onClick={() => { setImportOpen((v) => !v); setImportText("") }}
                  className="whitespace-nowrap"
                >
                  Import
                </Button>
                {importOpen && (
                  <div className="absolute right-0 top-full mt-2 z-[500] bg-admin_paper border border-admin_bordered rounded-lg shadow-xl p-3 flex flex-col gap-2 w-80">
                    <div className="flex gap-1">
                      {[["kv", "Key / Value"], ["keys", "Keys only"], ["values", "Values only"]].map(([m, label]) => (
                        <button
                          key={m}
                          onClick={() => setImportMode(m)}
                          className={`px-2 py-1 text-xs rounded border transition ${importMode === m ? "bg-admin_primary text-white border-admin_primary" : "border-admin_bordered text-admin_text_secondary hover:bg-admin_neutral_1"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-admin_text_secondary">
                      {importMode === "kv"
                        ? "One pair per line: key,value"
                        : importMode === "keys"
                        ? "One key per line — fills the key column in order, appends extras."
                        : "One value per line — fills the value column in order, appends extras."}
                    </p>
                    <textarea
                      autoFocus
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      rows={6}
                      className="w-full px-2 py-1.5 text-sm border border-admin_bordered rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text resize-y font-mono"
                      placeholder={importMode === "kv" ? "name,John\nage,30" : importMode === "keys" ? "name\nage" : "John\n30"}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setImportOpen(false)}
                        className="px-3 py-1 text-xs rounded border border-admin_bordered text-admin_text_secondary hover:bg-admin_neutral_1 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleImport}
                        disabled={!importText.trim()}
                        className="px-3 py-1 text-xs rounded bg-admin_primary text-white hover:bg-admin_primary/90 transition disabled:opacity-50"
                      >
                        Import
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedLang === primaryLanguage && (
              <Button
                variant="admin_secondary"
                onClick={() => {
                  setSearchTerm("")
                  fieldEditorRef.current?.addItem?.()
                }}
                className="whitespace-nowrap"
              >
                Add Item
              </Button>
            )}
            {selectedLang === primaryLanguage && value && value.length > 0 && (
              <Button
                variant="admin_secondary"
                onClick={() => {
                  setSearchTerm("")
                  fieldEditorRef.current?.clearAll?.()
                }}
                className="whitespace-nowrap"
              >
                Clear All
              </Button>
            )}
          </div>
        </div>

        {FieldEditor && (
          <div className="h-[calc(100vh-300px)] overflow-hidden">
            {selectedLang === primaryLanguage ? (
              // Primary language - show only the editor with full controls
              <div className="h-full overflow-y-auto pr-2">
                <LocalizationObjectEditor
                  key={`primary-${selectedLang}`}
                  ref={fieldEditorRef}
                  editButtonTitle={`Edit ${controller.getLanguageName(selectedLang)}`}
                  field={field}
                  value={filteredValue}
                  fieldFreezeStates={fieldFreezeStates}
                  onFieldFreezeToggle={handleFieldFreezeToggle}
                  showAddBtn={true}
                  showClearBtn={true}
                  showItemDelete={true}
                  onChange={(changed) => {
                    if (changed === undefined) return
                    handleContentChangeWithSync(changed, isFiltered, value)
                  }}
                />
              </div>
            ) : (
              // Non-primary language - show current language with inline reference support but limited controls
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto">
                  <LocalizationObjectEditor
                    key={`secondary-${selectedLang}`}
                    ref={fieldEditorRef}
                    editButtonTitle={`Edit ${controller.getLanguageName(selectedLang)}`}
                    field={field}
                    value={filteredValue}
                    fieldFreezeStates={fieldFreezeStates}
                    onFieldFreezeToggle={handleFieldFreezeToggle}
                    showInfo={false}
                    showAddBtn={false}
                    showClearBtn={false}
                    showItemDelete={false}
                    primaryValue={primaryValue}
                    primaryLanguage={primaryLanguage}
                    controller={controller}
                    selectedLang={selectedLang}
                    localizationKey={localizationKey}
                    onChange={(changed) => {
                      if (changed === undefined) return
                      handleContentChangeWithSync(changed, isFiltered, value)
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
